import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../lib/i18n'

/**
 * Mapa azimutal equidistante centrado no QTH.
 *
 * A projeção não é escolha estética: nela o azimute vira ângulo e a distância
 * vira raio. É literalmente o instrumento de apontar antena — o operador lê a
 * direção do alvo direto do desenho. Mercator seria bonito e inútil aqui.
 *
 * Tudo que aparece sai do mesmo motor que decide quem ouve quem: o terminador
 * usa a mesma função solar da absorção da camada D, e a cobertura vem do link
 * budget. O mapa e o rádio nunca discordam.
 */

const RINGS_KM = [2500, 5000, 10000, 15000, 20000]
const MAX_KM = 20000

export interface MapData {
  center: { lat: number; lon: number }
  grid: string
  freq_mhz: number
  daylight: boolean
  subsolar: { lat: number; lon: number }
  terminator: Array<{ lat: number; lon: number }>
  grey_line: {
    factor_now: number
    active: boolean
    next_kind?: string
    minutes_until?: number
  }
  coverage: Array<{
    bearing_deg: number
    range_km: number
    near_km: number
    skip_from_km?: number
    skip_to_km?: number
  }>
  beacons: Array<{
    callsign: string
    distance_km: number
    bearing_deg: number
    audible: boolean
    s_meter?: string
    mechanism?: string
    note?: string
  }>
  repeaters: Array<{ callsign: string; distance_km: number; bearing_deg: number }>
}

export function MapView({ lat, lon, freqKHz }: { lat: number; lon: number; freqKHz: number }) {
  const { t } = useI18n()
  const ref = useRef<HTMLCanvasElement>(null)
  const [data, setData] = useState<MapData | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    const load = () => {
      const q = new URLSearchParams({
        lat: String(lat),
        lon: String(lon),
        freq_khz: String(freqKHz),
      })
      fetch(`/api/map?${q}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d: MapData) => alive && setData(d))
        .catch((e: unknown) => alive && setErr(e instanceof Error ? e.message : 'falhou'))
    }
    load()
    // O terminador anda; recarregar de minuto em minuto basta.
    const t = window.setInterval(load, 60_000)
    return () => {
      alive = false
      clearInterval(t)
    }
  }, [lat, lon, freqKHz])

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !data) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    draw(ctx, canvas, data)
  }, [data])

  return (
    <div className="map">
      <canvas ref={ref} width={600} height={600} style={{ width: '100%', display: 'block' }} />
      {err && <p className="denial">{t.mapView.mapaIndisponivel} {err}</p>}
      {data && (
        <div className="map-meta">
          <span>{data.grid}</span>
          <span>{data.daylight ? t.mapView.dia : t.mapView.noite}</span>
          <span>
            {data.grey_line.active
              ? t.mapView.greyLineAgora
              : data.grey_line.minutes_until !== undefined
                ? `${data.grey_line.next_kind} ${t.mapView.doSolEm} ${data.grey_line.minutes_until} ${t.mapView.min}`
                : '—'}
          </span>
        </div>
      )}
    </div>
  )
}

/** Projeta (distância, azimute) em pixel. Azimute 0 para cima, horário. */
function project(cx: number, cy: number, r: number, distKm: number, bearingDeg: number) {
  const rad = (bearingDeg - 90) * (Math.PI / 180)
  const d = (Math.min(distKm, MAX_KM) / MAX_KM) * r
  return [cx + d * Math.cos(rad), cy + d * Math.sin(rad)] as const
}

/** Distância e azimute de grande círculo entre dois pontos. */
function greatCircle(from: { lat: number; lon: number }, to: { lat: number; lon: number }) {
  const R = 6371
  const toRad = Math.PI / 180
  const la1 = from.lat * toRad
  const la2 = to.lat * toRad
  const dLon = (to.lon - from.lon) * toRad
  const dLat = (to.lat - from.lat) * toRad

  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2
  const dist = 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))

  const y = Math.sin(dLon) * Math.cos(la2)
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon)
  const brg = (Math.atan2(y, x) / toRad + 360) % 360
  return { dist, brg }
}

function draw(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, d: MapData): void {
  const w = canvas.width
  const h = canvas.height
  const cx = w / 2
  const cy = h / 2
  const R = Math.min(w, h) / 2 - 24

  ctx.fillStyle = '#0d1014'
  ctx.fillRect(0, 0, w, h)

  // Cobertura: polígono do alcance por azimute. Não é círculo, e é esse o ponto.
  if (d.coverage.length) {
    ctx.beginPath()
    d.coverage.forEach((c, i) => {
      const [x, y] = project(cx, cy, R, c.range_km, c.bearing_deg)
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    })
    ctx.closePath()
    ctx.fillStyle = 'rgba(224, 145, 58, 0.13)'
    ctx.fill()
    ctx.strokeStyle = 'rgba(224, 145, 58, 0.55)'
    ctx.lineWidth = 1.5
    ctx.stroke()

    // Zona de silêncio: o buraco entre o NVIS e o DX.
    const skips = d.coverage.filter((c) => c.skip_from_km && c.skip_to_km)
    if (skips.length === d.coverage.length) {
      ctx.beginPath()
      d.coverage.forEach((c, i) => {
        const [x, y] = project(cx, cy, R, c.skip_to_km ?? 0, c.bearing_deg)
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      })
      ctx.closePath()
      d.coverage
        .slice()
        .reverse()
        .forEach((c, i) => {
          const [x, y] = project(cx, cy, R, c.skip_from_km ?? 0, c.bearing_deg)
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
        })
      ctx.closePath()
      ctx.fillStyle = '#0d1014'
      ctx.fill('evenodd')
    }
  }

  // Terminador: cada ponto projetado pela sua própria distância e azimute.
  if (d.terminator.length) {
    ctx.beginPath()
    let started = false
    for (const t of d.terminator) {
      const { dist, brg } = greatCircle(d.center, t)
      const [x, y] = project(cx, cy, R, dist, brg)
      if (!started) {
        ctx.moveTo(x, y)
        started = true
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.strokeStyle = 'rgba(255, 214, 130, 0.75)'
    ctx.lineWidth = 2
    ctx.setLineDash([6, 4])
    ctx.stroke()
    ctx.setLineDash([])
  }

  // Anéis de distância e raios de azimute.
  ctx.strokeStyle = 'rgba(155, 164, 175, 0.22)'
  ctx.lineWidth = 1
  ctx.font = '10px ui-monospace, monospace'
  ctx.fillStyle = 'rgba(155, 164, 175, 0.6)'
  for (const km of RINGS_KM) {
    const r = (km / MAX_KM) * R
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.stroke()
    ctx.fillText(`${km / 1000}k`, cx + 3, cy - r + 11)
  }
  for (let b = 0; b < 360; b += 30) {
    const [x, y] = project(cx, cy, R, MAX_KM, b)
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.lineTo(x, y)
    ctx.stroke()
    const [lx, ly] = project(cx, cy, R + 34, MAX_KM, b)
    ctx.fillStyle = 'rgba(155, 164, 175, 0.7)'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(b), lx, ly)
  }
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  // Repetidoras: pontos pequenos, tudo bem perto do centro.
  ctx.fillStyle = 'rgba(63, 178, 188, 0.8)'
  for (const rp of d.repeaters) {
    const [x, y] = project(cx, cy, R, rp.distance_km, rp.bearing_deg)
    ctx.beginPath()
    ctx.arc(x, y, 2, 0, Math.PI * 2)
    ctx.fill()
  }

  // Faróis: cheio quando audível agora, contorno quando não.
  for (const b of d.beacons) {
    const [x, y] = project(cx, cy, R, b.distance_km, b.bearing_deg)
    ctx.beginPath()
    ctx.arc(x, y, b.audible ? 5 : 3, 0, Math.PI * 2)
    if (b.audible) {
      ctx.fillStyle = '#e0913a'
      ctx.fill()
      ctx.fillStyle = '#e6eaef'
      ctx.font = '10px ui-monospace, monospace'
      ctx.fillText(b.callsign, x + 8, y + 3)
    } else {
      ctx.strokeStyle = 'rgba(155, 164, 175, 0.5)'
      ctx.lineWidth = 1
      ctx.stroke()
    }
  }

  // Você, no centro.
  ctx.beginPath()
  ctx.arc(cx, cy, 4, 0, Math.PI * 2)
  ctx.fillStyle = '#4ade80'
  ctx.fill()
}
